import { useEffect, useState } from 'react';
import { ModelViewer } from './ModelViewer';
import { MmdlViewer } from './MmdlViewer';

// 3D mesh formats rendered by the interactive three.js viewer (loaded on demand).
const MESH_3D_EXTS = ['stl', 'obj', 'glb', 'gltf', '3mf'];
// MiTek SAPPHIRE / BC Framer truss-design models — plan-view preview.
const MMDL_EXTS = ['mmdl', 'mmdf'];

interface ViewableDoc {
  id: number;
  name: string;
  type: string;
  url: string;
}

interface DocumentViewerModalProps {
  doc: ViewableDoc | null;
  onClose: () => void;
  onDownload?: (doc: ViewableDoc) => void;
}

function getViewerContent(doc: ViewableDoc) {
  const mime = doc.type.toLowerCase();
  const ext = doc.name.split('.').pop()?.toLowerCase() ?? '';

  if (MESH_3D_EXTS.includes(ext)) {
    return <ModelViewer url={doc.url} ext={ext} />;
  }

  if (MMDL_EXTS.includes(ext)) {
    return <MmdlViewer url={doc.url} name={doc.name} docId={doc.id} />;
  }

  if (mime.startsWith('image/') || ['jpg','jpeg','png','gif','webp','bmp','tiff','heic','heif','svg'].includes(ext)) {
    return (
      <img
        src={doc.url}
        alt={doc.name}
        className="max-w-full max-h-full object-contain rounded"
      />
    );
  }

  if (mime === 'application/pdf' || ext === 'pdf') {
    return (
      <iframe
        src={doc.url}
        title={doc.name}
        className="w-full h-full rounded border-0"
      />
    );
  }

  if (['doc','docx','xls','xlsx','ppt','pptx'].includes(ext)) {
    const encoded = encodeURIComponent(`${window.location.origin}${doc.url}`);
    return (
      <iframe
        src={`https://docs.google.com/viewer?url=${encoded}&embedded=true`}
        title={doc.name}
        className="w-full h-full rounded border-0"
      />
    );
  }

  if (mime.startsWith('text/') || ['txt','csv','log'].includes(ext)) {
    return (
      <iframe
        src={doc.url}
        title={doc.name}
        className="w-full h-full rounded border-0 bg-white"
      />
    );
  }

  return (
    <div className="flex flex-col items-center justify-center h-full gap-4 text-gray-500">
      <span className="material-symbols-outlined text-6xl">description</span>
      <p className="text-lg font-medium">{doc.name}</p>
      <p className="text-sm">Preview not available for this file type</p>
    </div>
  );
}

export const DocumentViewerModal = ({ doc, onClose, onDownload }: DocumentViewerModalProps) => {
  const [fullscreen, setFullscreen] = useState(false);
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  if (!doc) return null;

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center bg-black/70 ${fullscreen ? 'p-0' : 'p-4'}`}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className={`bg-white shadow-2xl flex flex-col w-full ${fullscreen ? 'max-w-none h-full rounded-none' : 'rounded-xl max-w-5xl h-[90vh]'}`}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <span className="material-symbols-outlined text-gray-400">
              {doc.type.startsWith('image/') ? 'image' : doc.type === 'application/pdf' ? 'picture_as_pdf' : 'description'}
            </span>
            <span className="font-semibold text-gray-900 truncate">{doc.name}</span>
          </div>
          <div className="flex items-center gap-2 shrink-0 ml-3">
            {onDownload && (
              <button
                onClick={() => onDownload(doc)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
              >
                <span className="material-symbols-outlined text-base">download</span>
                Download
              </button>
            )}
            <button
              onClick={() => setFullscreen(f => !f)}
              title={fullscreen ? 'Exit full screen' : 'Full screen'}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
            >
              <span className="material-symbols-outlined text-base">{fullscreen ? 'fullscreen_exit' : 'fullscreen'}</span>
              <span className="hidden sm:inline">{fullscreen ? 'Exit' : 'Full screen'}</span>
            </button>
            <a
              href={doc.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
            >
              <span className="material-symbols-outlined text-base">open_in_new</span>
              <span className="hidden sm:inline">Open</span>
            </a>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <span className="material-symbols-outlined text-gray-500">close</span>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden p-4 bg-gray-50 rounded-b-xl">
          {getViewerContent(doc)}
        </div>
      </div>
    </div>
  );
};
